import { useEffect, useState } from "react";
import { Download, X, Smartphone, Share, Plus } from "lucide-react";
import { useBranding } from "../context/TenantBrandingContext";
import { useTheme } from "../context/ThemeContext";

const DISMISSED_KEY = "hv.installPromptDismissedAt";
const DISMISS_DAYS = 14;

function isStandalone() {
  if (typeof window === "undefined") return false;
  // iOS Safari uses navigator.standalone, the rest use display-mode media query.
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isiOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isiOSSafari() {
  if (!isiOS()) return false;
  const ua = navigator.userAgent || "";
  // iOS Safari (not Chrome iOS, FxiOS etc.)
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

/**
 * Listens for `beforeinstallprompt` (Chromium/Android/Edge) and shows our
 * own branded install card. On iOS Safari (no event), we surface a
 * one-time guidance card with the "Add to Home Screen" steps.
 *
 * Auto-hides:
 *   • after install (`appinstalled` event)
 *   • when running in standalone mode already
 *   • for 14 days after the user dismisses
 */
export default function InstallPrompt() {
  const { branding } = useBranding();
  const { theme: promptTheme } = useTheme();
  const brandName = branding?.app_name || "Rogue";
  // [Iter 290zp.13t] Theme-aware logo for the install prompt card.
  const brandLogo =
    (promptTheme === "light"
      ? (branding?.logo_url_light || branding?.logo_url)
      : (branding?.logo_url_dark || branding?.logo_url))
    || "/icons/icon-192.png";
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [iosVisible, setiosVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;
    // Respect a recent dismissal
    const last = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
    if (last && Date.now() - last < DISMISS_DAYS * 24 * 60 * 60 * 1000) {
      return undefined;
    }

    const onBefore = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setOpen(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // Cleared after a successful install
    const onInstalled = () => {
      setOpen(false);
      setDeferredPrompt(null);
      setiosVisible(false);
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari has no event — show after a short delay so it doesn't
    // crowd the first paint. (Only if not already standalone.)
    let iosTimer = null;
    if (isiOSSafari()) {
      iosTimer = setTimeout(() => {
        setiosVisible(true);
        setOpen(true);
      }, 3500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setOpen(false);
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (_) {
      /* ignore */
    }
    setDeferredPrompt(null);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 sm:bottom-4 sm:right-4 sm:left-auto sm:max-w-sm z-40 px-3 pb-3 sm:px-0 sm:pb-0 hv-safe-bottom hv-safe-x"
      data-testid="install-prompt"
    >
      <div className="hv-card p-4 shadow-2xl border-[#1FB6C1]/40 relative bg-gradient-to-br from-[#121419] to-[#0a0b0e]">
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-2 right-2 text-[#647087] hover:text-white p-1"
          title="Dismiss"
          data-testid="install-prompt-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3">
          <img
            src={brandLogo}
            alt={brandName}
            className="w-12 h-12 rounded-md shrink-0 border border-white/10"
          />
          <div className="min-w-0">
            <div className="font-display text-base font-bold leading-tight">
              Install {brandName}
            </div>
            <p className="text-xs text-[#a0aabf] mt-0.5">
              {iosVisible
                ? `Add ${brandName} to your home screen — works offline and feels just like a native app.`
                : "One-tap install. Works offline, opens in its own window, and gets you back to work in seconds."}
            </p>
          </div>
        </div>

        {iosVisible ? (
          <ol className="mt-3 space-y-1.5 text-[11px] text-[#dde3ec]">
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#1FB6C1]/20 border border-[#1FB6C1]/40 text-[#1FB6C1] inline-flex items-center justify-center font-bold text-[10px] shrink-0">
                1
              </span>
              <span>
                Tap the <Share className="w-3.5 h-3.5 inline mx-0.5 text-[#1FB6C1]" /> Share button at the bottom of Safari.
              </span>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#1FB6C1]/20 border border-[#1FB6C1]/40 text-[#1FB6C1] inline-flex items-center justify-center font-bold text-[10px] shrink-0">
                2
              </span>
              <span>
                Scroll and tap{" "}
                <span className="font-bold">
                  Add to Home Screen <Plus className="w-3 h-3 inline" />
                </span>
                .
              </span>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#1FB6C1]/20 border border-[#1FB6C1]/40 text-[#1FB6C1] inline-flex items-center justify-center font-bold text-[10px] shrink-0">
                3
              </span>
              <span>
                Hit <span className="font-bold">Add</span> — done. Open {brandName} from your home screen anytime.
              </span>
            </li>
          </ol>
        ) : (
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={install}
              className="hv-btn hv-btn-primary text-xs flex-1"
              data-testid="install-prompt-install"
            >
              <Download className="w-3.5 h-3.5" /> Install now
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="install-prompt-later"
            >
              Maybe later
            </button>
          </div>
        )}

        <div className="mt-2 flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-[#647087]">
          <Smartphone className="w-3 h-3" />
          <span>Works on phone, tablet & desktop</span>
        </div>
      </div>
    </div>
  );
}
